<script setup lang="ts">
import { inject, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { processPermissionsDeniedApi } from '@Api/idv.api';
import { getHostRouter } from '@Src/mfe.config';
import { FlowLayout } from '@Shared';
import type { DashboardContext } from '@Shared';
import TechnicalDifficulties from '@Assets/images/technical-difficulties.svg';
import { LINKS } from '@Constants/links.constants';

const router = useRouter();
const currentRoute = useRoute();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const isLoading = ref(false);

// Same provider ProcessingCompleteView / useDashboardContext read — tells us
// whether this match runs inside the participant portal (a deal with more tasks)
// vs a standalone white-label single-request.
const getDashboardContext = inject<() => Promise<DashboardContext>>(
    'backToDashboard:getDashboardContext',
    () => Promise.resolve({ participantPortalEnabled: false, dealId: null }),
);

// "Try again": restart capture from doc-type selection. The request stays In
// Progress — a retry is an active attempt, not a failure, so nothing is persisted.
const goBack = () => {
    router.push({ name: 'match-idv-select-doc-type' });
};

// DS-4995: only an explicit "end this request" persists the failure. Mark the
// request UnableToVerify (reason PermissionsDenied) + flip the deal task to
// "failed", then send the participant to the dashboard so they see the failed
// task (and its "Retry verification" CTA). Note: no work happens on mount — the
// user stays on this screen until they choose, so nothing can bounce them off it.
const handlePermissionDenied = async () => {
    isLoading.value = true;
    try {
        await processPermissionsDeniedApi();
    } catch (error) {
        console.error('[Request MFE] Failed to process permissions denied:', error);
    }

    // Defaults to non-dashboard on any failure (mirrors useDashboardContext).
    const dashboardContext = await getDashboardContext()
        .catch(() => ({ participantPortalEnabled: false, dealId: null }));

    if (dashboardContext?.participantPortalEnabled === true) {
        const hostRouter = getHostRouter();
        if (hostRouter) {
            hostRouter.push({
                name: 'dashboard',
                params: { companySlug: hostRouter.currentRoute.value.params.companySlug },
            });
            return;
        }
    }

    // Standalone white-label single-request flow — no dashboard to return to.
    router.push({ name: 'match-idv-ended', params: { requestType: currentRoute.params.requestType } });
};
</script>

<template>
    <flow-layout
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :show-back-button="false">

        <app-form-content-layout>
            <div class="permission-denied-view">
                <img
                    alt="Something went wrong"
                    class="permission-denied-graphic"
                    :src="TechnicalDifficulties" />
                <div class="permission-denied-view__text-container">
                    <h1 data-id="permission-denied-title">We're unable to complete your verification</h1>
                    <p data-id="permission-denied-description">
                        Please try again. If the issue persists, your closing team will reach out to discuss next steps.
                    </p>
                </div>

                <div class="permission-denied-view__actions">
                    <app-button
                        expanded
                        size="large"
                        type="primary"
                        data-id="permission-denied-back-button"
                        @onClick="goBack">
                        Try again
                    </app-button>
                    <p class="permission-denied-view__help-text" data-id="permission-denied-camera-help">
                        Camera access may be turned off.
                        <a
                            :href="LINKS.CertifID.CameraPermissionsHelp"
                            target="_blank"
                            rel="noopener noreferrer">Here's how to turn it back on.</a>
                    </p>
                    <p class="permission-denied-view__help-text" data-id="permission-denied-camera-opt-out">
                        Don't want to use your camera?
                        <a href="#" @click.prevent="handlePermissionDenied">Click here to end this request.</a>
                    </p>
                </div>
            </div>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.permission-denied-view {
    display: flex;
    flex-direction: column;
    height: 100%;

    &__text-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    &__actions {
        margin-top: 2rem;
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    &__help-text {
        font-size: 1rem;
        line-height: 1.5;
        color: $black-high;

        a {
            color: var(--ch-primary-dark);
            text-decoration: underline;
            cursor: pointer;
        }
    }

    .permission-denied-graphic {
        width: 9.25rem;
        height: 9.25rem;
        align-self: center;
        margin-bottom: 1.5rem;
    }
}
</style>
